import { useCursoStore } from "@/store/cursoStore";
import {
  canAccessGuardianWithdrawals,
  canAccessKioskModule,
  isCpaDirectiva,
} from "@/utils/parseCourseCode";
import { ROUTE_ACCESS_REASON } from "@/utils/routeAccessDenied";
import {
  canActAsDirectivaConfig,
  canActAsDirectivaGestion,
  canActAsMiembroDirectiva,
  hasRoleInCurso,
  isMiembroCurso,
} from "@/utils/courseRoles";
import { isPlatformAdmin } from "./platformAdmin";
import { authService } from "@/services/authService";
import { clearStaleAuthState } from "@/utils/sessionCacheService";
import { getMessagingMe } from "@/services/messagingApiService";

/** Sync: idToken vigente o refresh disponible (UI / redirects). */
export const isAuthenticated = () => authService.isAuthenticated();

const clearAuthAndRedirectLogin = (next, redirectPath) => {
  authService.clearSessionTokens();
  clearStaleAuthState();
  next({ path: "/login", query: { redirect: redirectPath } });
};

const getUserEmail = () => localStorage.getItem("userEmail") || "";

const collectMetaFlag = (to, key) =>
  to.matched.some((record) => record.meta[key] === true);

const collectMetaRoles = (to) =>
  to.matched.flatMap((record) => record.meta.requiresRoles ?? []);

const collectMetaTreasuryType = (to) => {
  const matched = to.matched
    .map((record) => record.meta.requiresTreasuryType)
    .filter(Boolean);
  return matched.length > 0 ? matched[matched.length - 1] : null;
};

const redirectToMain = (next, reason, attemptedPath) => {
  next({
    name: "main-view",
    query: {
      reason,
      ...(attemptedPath ? { redirect: attemptedPath } : {}),
    },
  });
};

/**
 * Guard unificado: JWT (con refresh), admin, curso, rol, CPA y treasury_type.
 */
export const navigationGuard = async (to, _from, next) => {
  const requiresAuth = collectMetaFlag(to, "requiresAuth");
  const requiresAdmin = collectMetaFlag(to, "requiresAdmin");
  const requiresSupportAgent = collectMetaFlag(to, "requiresSupportAgent");
  const requiresCurso = collectMetaFlag(to, "requiresCurso");
  const requiresDirectiva = collectMetaFlag(to, "requiresDirectiva");
  const requiresDirectivaConfig = collectMetaFlag(to, "requiresDirectivaConfig");
  const requiresDirectivaMiembro = collectMetaFlag(
    to,
    "requiresDirectivaMiembro",
  );
  const requiresCpaDirectiva = collectMetaFlag(to, "requiresCpaDirectiva");
  const requiresKioskModule = collectMetaFlag(to, "requiresKioskModule");
  const requiresPupilCourse = collectMetaFlag(to, "requiresPupilCourse");
  const requiresRoles = collectMetaRoles(to);
  const requiresTreasuryType = collectMetaTreasuryType(to);

  if (requiresAuth) {
    const sessionOk = await authService.ensureValidSession();
    if (!sessionOk) {
      clearAuthAndRedirectLogin(next, to.fullPath);
      return;
    }
  }

  if (requiresAdmin && !isPlatformAdmin()) {
    redirectToMain(next, ROUTE_ACCESS_REASON.FORBIDDEN_ADMIN, to.fullPath);
    return;
  }

  if (requiresSupportAgent) {
    if (isPlatformAdmin()) {
      // ok
    } else {
      try {
        const me = await getMessagingMe();
        if (!me?.isSupportAgent) {
          redirectToMain(
            next,
            ROUTE_ACCESS_REASON.FORBIDDEN_ADMIN,
            to.fullPath,
          );
          return;
        }
      } catch {
        redirectToMain(next, ROUTE_ACCESS_REASON.FORBIDDEN_ADMIN, to.fullPath);
        return;
      }
    }
  }

  const cursoStore = useCursoStore();
  const curso = cursoStore.getCursoData();

  if (requiresCurso && !isMiembroCurso(curso)) {
    redirectToMain(next, ROUTE_ACCESS_REASON.NO_CURSO, to.fullPath);
    return;
  }

  if (requiresKioskModule && !canAccessKioskModule(curso)) {
    redirectToMain(
      next,
      ROUTE_ACCESS_REASON.FORBIDDEN_PUPIL_REQUIRED,
      to.fullPath,
    );
    return;
  }

  if (requiresPupilCourse && !canAccessGuardianWithdrawals(curso)) {
    redirectToMain(
      next,
      ROUTE_ACCESS_REASON.FORBIDDEN_PUPIL_REQUIRED,
      to.fullPath,
    );
    return;
  }

  if (requiresTreasuryType && curso?.treasury_type !== requiresTreasuryType) {
    redirectToMain(
      next,
      ROUTE_ACCESS_REASON.FORBIDDEN_TESORERIA_TYPE,
      to.fullPath,
    );
    return;
  }

  const userEmail = getUserEmail();

  if (requiresCpaDirectiva && !isCpaDirectiva(curso)) {
    redirectToMain(next, ROUTE_ACCESS_REASON.FORBIDDEN_CPA, to.fullPath);
    return;
  }

  // CPA con gestión (incluye director) puede usar rutas `requiresDirectiva`
  // sin ampliar esos permisos en grupos curso normales.
  if (
    requiresDirectiva &&
    !canActAsDirectivaGestion(curso, userEmail) &&
    !isCpaDirectiva(curso)
  ) {
    redirectToMain(next, ROUTE_ACCESS_REASON.FORBIDDEN_DIRECTIVA, to.fullPath);
    return;
  }

  if (requiresDirectivaConfig && !canActAsDirectivaConfig(curso, userEmail)) {
    redirectToMain(
      next,
      ROUTE_ACCESS_REASON.FORBIDDEN_DIRECTIVA_CONFIG,
      to.fullPath,
    );
    return;
  }

  if (requiresDirectivaMiembro && !canActAsMiembroDirectiva(curso, userEmail)) {
    redirectToMain(
      next,
      ROUTE_ACCESS_REASON.FORBIDDEN_DIRECTIVA,
      to.fullPath,
    );
    return;
  }

  if (
    requiresRoles.length > 0 &&
    !hasRoleInCurso(curso, userEmail, requiresRoles)
  ) {
    redirectToMain(next, ROUTE_ACCESS_REASON.FORBIDDEN_ROLE, to.fullPath);
    return;
  }

  next();
};

export default navigationGuard;
